import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function Search() {
  const { tenant, pricing, refreshTenant } = useAuth();
  const navigate = useNavigate();

  const [lob, setLob] = useState('restaurant');
  const [searchInput, setSearchInput] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [addOns, setAddOns] = useState({ property_data: false, trip: false });
  const [isSearching, setIsSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    async function loadHistory() {
      try {
        const res = await api.get('/classify/history', { params: { limit: 5 } });
        setRecentSearches(res.data.searches || []);
      } catch (error) {
        console.error('History load error:', error);
      } finally {
        setLoadingHistory(false);
      }
    }
    loadHistory();
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleInputChange = useCallback((value) => {
    setSearchInput(value);
    setSelectedPlace(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/classify/autocomplete', { params: { input: value } });
        setSuggestions(res.data.suggestions || []);
        setShowSuggestions(true);
        setActiveIndex(-1);
      } catch (error) { console.error('Autocomplete error:', error); }
    }, 300);
  }, []);

  const handleSelectPlace = (suggestion) => {
    setSelectedPlace(suggestion);
    setSearchInput(suggestion.text || suggestion.structured?.main || '');
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(prev => Math.min(prev + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(prev => Math.max(prev - 1, 0)); }
    else if (e.key === 'Enter' && activeIndex >= 0) { e.preventDefault(); handleSelectPlace(suggestions[activeIndex]); }
    else if (e.key === 'Escape') setShowSuggestions(false);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!selectedPlace?.place_id) { toast.error('Please select a business from the suggestions'); return; }
    if (!lob) { toast.error('Please select a Line of Business'); return; }
    setIsSearching(true);
    const searchToast = toast.loading('Generating classification report...');
    try {
      const res = await api.post('/classify/search', {
        lob,
        place_id: selectedPlace.place_id,
        business_name: selectedPlace.structured?.main || searchInput,
        add_ons: addOns
      });
      toast.dismiss(searchToast);
      toast.success(`Report generated in ${(res.data.elapsed_ms / 1000).toFixed(1)}s`);
      refreshTenant();
      navigate(`/report/${res.data.search_id}`);
    } catch (error) {
      toast.dismiss(searchToast);
      toast.error(error.response?.data?.error || 'Search failed');
    } finally {
      setIsSearching(false);
    }
  };

    const estimatedCost =
        (addOns.property_data ? (pricing?.property_data || 7) : 0) +
        (addOns.trip ? (pricing?.trip || 2) : 0);

  // fix #7: Food Retail tile subtitle
  const lobOptions = [
    { value: 'restaurant', emoji: '🍽️', label: 'Restaurants', subtitle: null },
    { value: 'food_retail', emoji: '🛒', label: 'Food Retail', subtitle: 'Grocery, convenience, specialty, etc.' },
    { value: 'office', emoji: '🏢', label: 'Offices', subtitle: null },
      { value: 'self_storage', emoji: '🏪', label: 'Self Storage', subtitle: 'Mini storage, climate-controlled, etc.' },
      { value: 'hotel', emoji: '🏨', label: 'Hotels', subtitle: 'Hotels, motels, extended-stay, etc.' },
  ];

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="font-heading text-2xl text-navy">Business Classification Search</h1>
        <p className="text-gray-500 mt-1">Search for a business to generate a classification report.</p>
      </div>

      <form onSubmit={handleSearch} className="card mb-8">
        {/* LOB Selector */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">Line of Business</label>
          <div className="grid grid-cols-2 gap-3">
            {lobOptions.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setLob(opt.value)}
                className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all text-left ${
                  lob === opt.value
                    ? 'border-teal bg-teal-50 text-teal-700'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300'
                }`}
              >
                <div>{opt.emoji} {opt.label}</div>
                {opt.subtitle && (
                  <div className="text-xs font-normal opacity-70 mt-0.5">{opt.subtitle}</div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Business Search */}
        <div className="mb-6 relative">
          <label className="block text-sm font-semibold text-gray-700 mb-1">Business</label>
          <input
            ref={inputRef}
            type="text"
            value={searchInput}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            className={`input-field ${selectedPlace ? 'border-green-300 bg-green-50' : ''}`}
            placeholder="Type business name and address..."
            autoComplete="off"
          />
          {selectedPlace && (
            <span className="absolute right-3 top-9 text-green-500 text-sm font-medium">✓ Selected</span>
          )}
          {showSuggestions && suggestions.length > 0 && (
            <div ref={suggestionsRef} className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-auto">
              {suggestions.map((s, i) => (
                <button
                  key={s.place_id || i}
                  type="button"
                  onClick={() => handleSelectPlace(s)}
                  className={`w-full text-left px-4 py-3 hover:bg-teal-50 border-b border-gray-50 last:border-0 transition-colors ${i === activeIndex ? 'bg-teal-50' : ''}`}
                >
                  <div className="font-medium text-navy text-sm">{s.structured?.main || s.text}</div>
                  {s.structured?.secondary && <div className="text-xs text-gray-400 mt-0.5">{s.structured.secondary}</div>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Add-Ons */}
        <div className="mb-6 bg-gray-50 rounded-lg p-4 space-y-3">
          <label className="block text-sm font-semibold text-gray-700 mb-1">Report Add-Ons</label>
          {tenant?.add_ons?.property_data && (
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={addOns.property_data}
                onChange={(e) => setAddOns(prev => ({ ...prev, property_data: e.target.checked }))}
                className="mt-1 w-4 h-4 text-teal rounded border-gray-300 focus:ring-teal"
              />
              <div>
                <span className="font-medium text-navy group-hover:text-teal transition-colors">
                  Add Property Data <span className="text-teal font-bold ml-1">+${(pricing?.property_data || 7).toFixed(2)}</span>
                </span>
                <p className="text-xs text-gray-400 mt-0.5">Building, hazards, fire protection, valuation</p>
                          </div>
                      </label>
                  )}
                  {tenant?.add_ons?.trip && (
                      <label className="flex items-start gap-3 cursor-pointer group">
                          <input
                              type="checkbox"
                              checked={addOns.trip}
                              onChange={(e) => setAddOns(prev => ({ ...prev, trip: e.target.checked }))}
                              className="mt-1 w-4 h-4 text-teal rounded border-gray-300 focus:ring-teal"
                          />
                          <div>
                              <span className="font-medium text-navy group-hover:text-teal transition-colors">
                                  Add TRIP Profile <span className="text-teal font-bold ml-1">+${(pricing?.trip || 2).toFixed(2)}</span>
                              </span>
                              <p className="text-xs text-gray-400 mt-0.5">Location-based trafficking risk profile</p>
                          </div>
                      </label>
                  )}
                  {estimatedCost > 0 && (
            <div className="pt-2 border-t border-gray-200 text-sm">
              <span className="text-gray-500">Add-on cost:</span>
              <span className="font-bold text-navy ml-1">${estimatedCost.toFixed(2)}</span>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={isSearching || !selectedPlace}
          className="btn-primary w-full text-lg py-3 flex items-center justify-center gap-2"
        >
          {isSearching ? (
            <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Generating Report...</>
          ) : (<>🔍 Generate Report</>)}
        </button>
      </form>

      {/* Usage Meter */}
      {tenant && (
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">📊 This Month's Usage</span>
            <span className="text-sm font-bold text-navy">{tenant.searches_used} / {tenant.searches_limit} searches</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                (tenant.searches_used / tenant.searches_limit) > 0.9 ? 'bg-red-500' :
                (tenant.searches_used / tenant.searches_limit) > 0.7 ? 'bg-yellow-500' : 'bg-teal'
              }`}
              style={{ width: `${Math.min(100, (tenant.searches_used / tenant.searches_limit) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Recent Searches */}
      <div className="card">
        <h3 className="font-heading text-lg text-navy mb-4">Recent Searches</h3>
        {loadingHistory ? (
          <div className="text-center py-8 text-gray-400">Loading...</div>
        ) : recentSearches.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p className="text-lg mb-1">No searches yet</p>
            <p className="text-sm">Your search history will appear here.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {recentSearches.map((search) => (
              <button
                key={search._id}
                onClick={() => navigate(`/report/${search._id}`)}
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-teal-50 border border-gray-100 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-navy group-hover:text-teal transition-colors">
                      {search.result?.verification?.google_name || search.input?.business_name || 'Unknown'}
                    </span>
                    <span className="text-gray-400 text-xs ml-2">{search.input?.city}, {search.input?.state}</span>
                  </div>
                  <span className="text-xs text-teal font-medium opacity-0 group-hover:opacity-100 transition-opacity">View Report →</span>
                </div>
                <div className="flex gap-2 mt-1 text-xs text-gray-400">
                  <span>{new Date(search.createdAt).toLocaleDateString()}</span>
                  <span>•</span>
                  <span className="capitalize">{search.input?.lob?.replace(/_/g, ' ')}</span>
                        {search.add_ons_used?.includes('property_data') && <span className="text-gold">+Prop</span>}
                        {search.add_ons_used?.includes('trip') && <span className="text-gold">+TRIP</span>}
                  {search.result?.classification?.primary && (
                    <>
                      <span>•</span>
                      <span className={
                        search.result.classification.confidence_level === 'high' ? 'text-green-600' :
                        search.result.classification.confidence_level === 'medium' ? 'text-yellow-600' : 'text-red-600'
                      }>
                        {search.result.classification.primary.code} ({search.result.classification.primary.confidence}%)
                      </span>
                    </>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
